import React, { useEffect } from "react";
import { Alert, Form, Input, Switch, Select, Row, Col, Divider, Tooltip } from "antd";
import { InfoCircleOutlined, ExportOutlined } from "@ant-design/icons";

const { TextArea } = Input;

const layout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
};

const defaultValues = {
  name: "",
  label: "",
  description: "",
  ofc_managed: false,
  plans: [],
  products: [],
  module_pointer: null,
  default: false,
  guide_article_id: "",
};

const FeatureEditForm = ({
  form,
  draftSeed,
  sessionKey,
  modules,
  plans,
  isCreating = false,
}) => {
  const selectedModuleId = Form.useWatch("module_pointer", form);
  const selectedModule = modules?.find((m) => m.objectId === selectedModuleId);
  const moduleLink = selectedModule
    ? `/settings/modules?q=${encodeURIComponent(selectedModule.name)}`
    : null;

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      name: draftSeed.name || defaultValues.name,
      label: draftSeed.label || defaultValues.label,
      description: draftSeed.description || defaultValues.description,
      ofc_managed: draftSeed.ofc_managed || defaultValues.ofc_managed,
      plans:
        draftSeed.plans?.map((plan) =>
          typeof plan === "string" ? plan : plan.value
        ) || defaultValues.plans,
      products:
        draftSeed.products?.map((product) =>
          typeof product === "string" ? product : product.value
        ) || defaultValues.products,
      module_pointer:
        draftSeed.module_pointer?.objectId || defaultValues.module_pointer,
      default: draftSeed.default || defaultValues.default,
      guide_article_id: draftSeed.guide_article_id || defaultValues.guide_article_id,
    });
  }, [form, draftSeed, sessionKey]);

  return (
    <Form form={form} name="feature_edit" layout="horizontal">
      <Row gutter={32}>
        <Col span={12}>
          <Divider orientation="left">Informazioni Base</Divider>
          <Form.Item
            {...layout}
            name="name"
            label={
              <span>
                Nome{" "}
                <Tooltip title="Identificativo tecnico nel formato gruppo.azione (es. servizi.create). Determina il raggruppamento nella lista.">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: "Inserisci il nome della feature!" },
            ]}
            extra="Convenzione: gruppo.azione (es. servizi.create)"
          >
            <Input placeholder="gruppo.azione" />
          </Form.Item>

          <Form.Item
            {...layout}
            name="label"
            label="Label"
            rules={[
              { required: true, message: "Inserisci la label della feature!" },
            ]}
          >
            <Input placeholder="Etichetta mostrata nell'app" />
          </Form.Item>

          <Form.Item
            {...layout}
            name="description"
            label={
              <span>
                Descrizione{" "}
                <Tooltip title="Descrizione dettagliata della feature">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
          >
            <TextArea rows={4} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Divider orientation="left">Configurazione</Divider>
          <Form.Item
            {...layout}
            name="ofc_managed"
            label={
              <span>
                OFC Managed{" "}
                <Tooltip title="Indica se la feature è gestita da OFC">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            {...layout}
            name="default"
            label={
              <span>
                Sempre abilitata{" "}
                <Tooltip title="Se attivo, la feature resta disponibile anche quando piano o modulo non la includono. Può comunque essere disabilitata da preferenze organizzazione o utente.">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {isCreating && (
            <Alert
              type="warning"
              showIcon
              message="Le nuove feature vanno abbinate ai moduli."
              description="Nel campo Plans seleziona legacy_upsell: gli altri piani sono una configurazione legacy da usare solo per mantenere feature esistenti basate su piano."
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item {...layout} name="plans" label="Plans">
            <Select
              mode="multiple"
              allowClear
              placeholder="Seleziona i piani"
              optionFilterProp="children"
              showSearch
            >
              <Select.Option key="legacy_upsell" value="legacy_upsell">
                legacy_upsell
              </Select.Option>
              {plans?.filter((plan) => plan !== "legacy_upsell").map((plan) => (
                <Select.Option key={plan} value={plan}>
                  {plan}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item {...layout} name="products" label="Products">
            <Select
              mode="multiple"
              allowClear
              placeholder="Seleziona i prodotti"
              optionFilterProp="children"
              showSearch
            >
              <Select.Option key="giotto" value="giotto">
                giotto
              </Select.Option>
              <Select.Option key="eterno" value="eterno">
                eterno
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            {...layout}
            name="guide_article_id"
            label="Intercom guida ID"
          >
            <Input type="number" placeholder="ID articolo guida Intercom" />
          </Form.Item>

          <Form.Item
            {...layout}
            name="module_pointer"
            label="Modulo"
            extra={
              moduleLink ? (
                <a href={moduleLink} target="_blank" rel="noopener noreferrer">
                  <ExportOutlined /> Apri "{selectedModule.name}" nei Moduli
                </a>
              ) : null
            }
          >
            <Select
              allowClear
              placeholder="Seleziona il modulo"
              optionFilterProp="children"
              showSearch
            >
              {modules?.map((module) => (
                <Select.Option key={module.objectId} value={module.objectId}>
                  {module.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default FeatureEditForm;
